import { createContext, ReactNode, useContext, useLayoutEffect, useState } from 'react'
import { ListItemIcon, ListItemText, MenuItem, MenuItemProps, MenuProps, SxProps, Theme } from '@mui/material'
import { AppMenu } from '../../common/AppMenu'
import { useSelector } from 'react-redux'
import { selectActiveChartId, selectChartConfig } from '../../../selectors/chart'
import { selectMultiPaneFullScreen } from '../../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../../assets/constants'
import { DRAWING_TOOLBAR_ICON_SIZE } from './drawingToolbarShared'
import { useAppContextChart } from '../useAppContextChart'

/** Shifts menu left of the default anchor so icons align with the toolbar button. */
export const DRAWING_TOOLBAR_MENU_HORIZONTAL_OFFSET = 4

type DrawingToolbarMenuLayoutContextValue = {
    opensToLeft: boolean
}

const DrawingToolbarMenuLayoutContext = createContext<DrawingToolbarMenuLayoutContextValue>({
    opensToLeft: false
})

function getChartPlotHorizontalBounds(
    anchorEl: HTMLElement,
    rightScaleWidth: number,
    leftScaleWidth: number,
    scaleOnLeft: boolean,
    showLeftScale: boolean,
    showRightScale: boolean
) {
    const chartEl = anchorEl.closest('.chart-selector') as HTMLElement | null
    if (!chartEl) {
        return { left: 0, right: window.innerWidth }
    }
    const chartRect = chartEl.getBoundingClientRect()
    let left = chartRect.left
    let right = chartRect.right

    if (showLeftScale && leftScaleWidth > 0) {
        left += leftScaleWidth
    }
    if (showRightScale && rightScaleWidth > 0 && !scaleOnLeft) {
        right -= rightScaleWidth
    }

    return { left, right }
}

export function useDrawingToolbarMenuPlacement(
    anchorEl: HTMLElement | null,
    open: boolean,
    estimatedMenuWidth = 200
) {
    const activeChartId = useSelector(selectActiveChartId)
    const chart = useAppContextChart(activeChartId ?? '')
    const chartConfig = useSelector(selectChartConfig(activeChartId ?? ''))
    const [opensToLeft, setOpensToLeft] = useState(false)
    const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null)

    useLayoutEffect(() => {
        if (!open || !anchorEl) {
            setOpensToLeft(false)
            setAnchorPosition(null)
            return
        }

        const rect = anchorEl.getBoundingClientRect()
        const showLeftScale = chartConfig?.scaleOptions?.leftScaleVisible ?? false
        const showRightScale = chartConfig?.scaleOptions?.rightScaleVisible ?? true
        const scaleOnLeft = showLeftScale && !showRightScale
        const rightScaleWidth = chart?.getRightScaleWidth() ?? 52
        const leftScaleWidth = chart?.getLeftScaleWidth() ?? 0
        const { left: plotLeft, right: plotRight } = getChartPlotHorizontalBounds(
            anchorEl,
            rightScaleWidth,
            leftScaleWidth,
            scaleOnLeft,
            showLeftScale,
            showRightScale
        )

        const spaceOnRight = plotRight - rect.left - DRAWING_TOOLBAR_MENU_HORIZONTAL_OFFSET
        const spaceOnLeft = rect.right - plotLeft - DRAWING_TOOLBAR_MENU_HORIZONTAL_OFFSET
        const shouldOpenToLeft = spaceOnRight < estimatedMenuWidth && spaceOnLeft >= estimatedMenuWidth

        setOpensToLeft(shouldOpenToLeft)
        setAnchorPosition({
            top: rect.top,
            left: shouldOpenToLeft
                ? rect.right + DRAWING_TOOLBAR_MENU_HORIZONTAL_OFFSET
                : rect.left - DRAWING_TOOLBAR_MENU_HORIZONTAL_OFFSET
        })
    }, [open, anchorEl, estimatedMenuWidth, chart, chartConfig])

    return {
        opensToLeft,
        anchorPosition,
        anchorOrigin: {
            vertical: 'top',
            horizontal: opensToLeft ? 'right' : 'left'
        } as const,
        transformOrigin: {
            vertical: 'bottom',
            horizontal: opensToLeft ? 'right' : 'left'
        } as const
    }
}

const menuItemIconSx: SxProps<Theme> = {
    minWidth: DRAWING_TOOLBAR_ICON_SIZE,
    width: DRAWING_TOOLBAR_ICON_SIZE,
    margin: 0,
    padding: 0,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    '& svg': {
        width: DRAWING_TOOLBAR_ICON_SIZE,
        height: DRAWING_TOOLBAR_ICON_SIZE
    },
    '& .MuiSvgIcon-root': {
        fontSize: DRAWING_TOOLBAR_ICON_SIZE
    }
}

const menuItemTextSx = (opensToLeft: boolean): SxProps<Theme> => ({
    flex: 1,
    m: 0,
    p: 0,
    minWidth: 0,
    textAlign: opensToLeft ? 'right' : 'left',
    whiteSpace: 'nowrap',
    '& .MuiListItemText-primary': {
        padding: 0,
        margin: 0
    }
})

const menuItemRowSx = (opensToLeft: boolean): SxProps<Theme> => ({
    flexDirection: opensToLeft ? 'row-reverse' : 'row',
    justifyContent: opensToLeft ? 'flex-end' : 'flex-start',
    alignItems: 'center',
    gap: '8px',
    py: 0.75,
    px: 1,
    minHeight: 36
})

type DrawingToolbarOptionsMenuProps = {
    anchorEl: HTMLElement | null
    open: boolean
    onClose: () => void
    estimatedWidth?: number
    children: ReactNode
} & Pick<MenuProps, 'MenuListProps'>

export const DrawingToolbarOptionsMenu = ({
    anchorEl,
    open,
    onClose,
    estimatedWidth,
    children,
    MenuListProps,
    ...menuProps
}: DrawingToolbarOptionsMenuProps) => {
    const container = useSelector(selectMultiPaneFullScreen)
    const { anchorPosition, anchorOrigin, transformOrigin, opensToLeft } = useDrawingToolbarMenuPlacement(
        anchorEl,
        open,
        estimatedWidth
    )
    return (
        <DrawingToolbarMenuLayoutContext.Provider value={{ opensToLeft }}>
            <AppMenu
                anchorReference={anchorPosition ? 'anchorPosition' : 'anchorEl'}
                anchorPosition={anchorPosition ?? undefined}
                anchorEl={anchorEl}
                open={open && Boolean(anchorPosition)}
                onClose={onClose}
                container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                marginThreshold={0}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                MenuListProps={{
                    dense: true,
                    ...MenuListProps,
                    sx: { py: 0.5, ...MenuListProps?.sx }
                }}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: estimatedWidth ?? 160
                        }
                    }
                }}
                {...menuProps}
            >
                {children}
            </AppMenu>
        </DrawingToolbarMenuLayoutContext.Provider>
    )
}

type DrawingToolbarMenuItemProps = MenuItemProps & {
    icon: ReactNode
    label: string
}

export const DrawingToolbarMenuItem = ({ icon, label, sx, ...menuItemProps }: DrawingToolbarMenuItemProps) => {
    const { opensToLeft } = useContext(DrawingToolbarMenuLayoutContext)

    return (
        <MenuItem sx={[menuItemRowSx(opensToLeft), ...(Array.isArray(sx) ? sx : [sx])]} {...menuItemProps}>
            <ListItemIcon sx={menuItemIconSx}>{icon}</ListItemIcon>
            <ListItemText primary={label} sx={menuItemTextSx(opensToLeft)} />
        </MenuItem>
    )
}

type DrawingToolbarMenuTextItemProps = MenuItemProps & {
    label: string
}

export const DrawingToolbarMenuTextItem = ({ label, sx, ...menuItemProps }: DrawingToolbarMenuTextItemProps) => {
    const { opensToLeft } = useContext(DrawingToolbarMenuLayoutContext)

    return (
        <MenuItem
            sx={[
                {
                    justifyContent: opensToLeft ? 'flex-end' : 'flex-start',
                    py: 0.75,
                    px: 1.5
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
            ]}
            {...menuItemProps}
        >
            <ListItemText primary={label} sx={menuItemTextSx(opensToLeft)} />
        </MenuItem>
    )
}
